import { VFC } from 'react';
import * as Icon from 'react-feather';

import { useSearchCondition } from '../../hooks/useSearchCondition';
import { Select } from '../atoms/Select';
import { Input } from '../atoms/Input';
import { CategorySelector } from './CategorySelector';
import { FieldSelector } from './FieldSelector';
import { OperatorSelector } from './OperatorSelector';

type ConditionBoxProps = { id: string; infoType: 'jian' | 'dispatchInfo' | 'patientInfo' };

const SingleChoiceBox = ({ id, options }: {id: string, options: string[]}) => {
  const {
    searchCondition,
    setMultipleValue,
  } = useSearchCondition(id);

  const setChoice = (value: string, index: number) => {
    const newValues = (searchCondition?.value ?? []).map((v, i) => (i === index ? value : v));
    setMultipleValue(newValues);
  }

  const addChoice = () => {
    setMultipleValue([...(searchCondition?.value ?? []), '']);
  }

  const removeChoice = (index: number) => {
    const newValues = (searchCondition?.value ?? []).filter((_, i) => i !== index);
    setMultipleValue(newValues);
  }

  const forms = (searchCondition?.value ?? []).map((v, i, me) =>  (
    <div key={`choice-${i}`} className="flex gap-2 w-96">
      <Select
        name={`single-choice-${i}`}
        value={v}
        onChange={(e) => setChoice(e.target.value, i)}
        required
      >
        <option value="" hidden>
          値を選択
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
      {i > 0 ? (
        <button
          type="button"
          className="flex items-center justify-center border-2 border-darkblue_100 text-darkblue_100 rounded w-8 h-8"
          onClick={() => removeChoice(i)}
        >
          <Icon.X size={24} className=" stroke-darkblue_100" />
        </button>
      ) : null}
    </div>
  ));

  return (
    <>
      {forms}
      <div className="flex gap-2 w-96 items-center">
        <button
          type="button"
          className="flex items-center justify-center border-2 border-darkblue_100 text-darkblue_100 rounded w-8 h-8"
          onClick={addChoice}
        >
          <Icon.Plus size={24} className=" stroke-darkblue_100" />
        </button>項目を追加
      </div>
    </>
  );
}

export const ConditionBox: VFC<ConditionBoxProps> = ({ id, infoType }) => {
  const {
    searchCondition,
    selectedField,
    setValue,
    setValueFrom,
    setValueTo,
    setMultipleValue,
    deleteCondition,
  } = useSearchCondition(id);

  return (
    <div className="flex items-start space-x-2 p-4 bg-darkblue_20 rounded justify-between">
      <div className="flex flex-wrap gap-2 w-96">
        <CategorySelector id={id} infoType={infoType} />
        <FieldSelector id={id} />
        {selectedField?.fieldType === 'STRING' ? (
          <div className="flex gap-2">
            <OperatorSelector id={id} type="string" />
            <Input
              type="text"
              className="w-52"
              value={searchCondition?.value[0]}
              onChange={(e) => setValue(e.target.value)}
              required
            />
          </div>
        ) : selectedField?.fieldType === 'SINGLECHOICE' ? (
          <SingleChoiceBox id={id} options={selectedField.options ?? []} />
        ) : selectedField?.fieldType === 'MULTIPLECHOICE' ? (
          <div className="flex gap-2">
            <OperatorSelector id={id} type="multiplechoice" />
            <Select
              name="multiple-choice"
              value={searchCondition?.value}
              onChange={(e) =>
                setMultipleValue(Array.from(e.target.selectedOptions, (option) => option.value))
              }
              multiple
              required
            >
              <option value="" hidden>
                値を選択
              </option>
              {selectedField.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </div>
        ) : selectedField?.fieldType === 'INT' ||
          selectedField?.fieldType === 'FLOAT' ||
          selectedField?.fieldType === 'DATE' ||
          selectedField?.fieldType === 'DATETIME' ? (
          <div className="flex gap-2">
            <OperatorSelector id={id} type="numeric" />
            {searchCondition?.operator !== 'between' ? (
              <Input
                type={
                  selectedField?.fieldType === 'DATE'
                    ? 'date'
                    : selectedField?.fieldType === 'DATETIME'
                    ? 'datetime-local'
                    : 'number'
                }
                value={searchCondition?.value[0]}
                step={selectedField?.fieldType === 'DATETIME' ? 1 : undefined}
                onChange={(e) => setValue(e.target.value)}
                className="w-52"
                required
              />
            ) : (
              <div className="flex flex-wrap gap-1 items-center">
                <div className="flex w-60 items-center">
                  <Input
                    type={
                      selectedField?.fieldType === 'DATE'
                        ? 'date'
                        : selectedField?.fieldType === 'DATETIME'
                        ? 'datetime-local'
                        : 'number'
                    }
                    placeholder="最小値"
                    value={searchCondition?.value[0]}
                    step={selectedField?.fieldType === 'DATETIME' ? 1 : undefined}
                    onChange={(e) => setValueFrom(e.target.value)}
                    className="w-52"
                    required
                  />
                </div>
                <div className="flex w-60 gap-1 items-center">
                  <div className="font-bold gap-4 text-darkblue_100">〜</div>
                  <Input
                    type={
                      selectedField?.fieldType === 'DATE'
                        ? 'date'
                        : selectedField?.fieldType === 'DATETIME'
                        ? 'datetime-local'
                        : 'number'
                    }
                    placeholder="最大値"
                    value={searchCondition?.value[1]}
                    step={selectedField?.fieldType === 'DATETIME' ? 1 : undefined}
                    onChange={(e) => setValueTo(e.target.value)}
                    className="w-52"
                    required
                  />
                </div>
              </div>
            )}
          </div>
        ) : null}
        {!(searchCondition?.isValid ?? true) ? (
          <div className="flex flex-wrap gap-2 w-96 text-yellow-600 place-content-end">
            <Icon.AlertTriangle size={24} />{searchCondition?.errorMessage}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="flex items-center justify-center border-2 border-darkblue_100 text-darkblue_100 rounded w-8 h-8"
        onClick={deleteCondition}
      >
        <Icon.X size={24} className=" stroke-darkblue_100" />
      </button>
    </div>
  );
};
